import { FC } from 'react';
import { Editor } from '@tiptap/react';
import { Button, Divider, Space, Tooltip, Upload } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  LinkOutlined,
  PictureOutlined,
  LineOutlined,
  UndoOutlined,
  RedoOutlined,
} from '@ant-design/icons';

import styles from './CommerceNoticeEditor.module.scss';

interface EditorToolbarProps {
  editor: Editor | null;
  onImageUpload: (file: File) => void;
  imageUploading: boolean;
}

const EditorToolbar: FC<EditorToolbarProps> = ({ editor, onImageUpload, imageUploading }) => {
  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL을 입력하세요', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className={styles.toolbar}>
      <Space size={2} wrap>
        <Tooltip title='Heading 1'>
          <Button
            type={editor.isActive('heading', { level: 1 }) ? 'primary' : 'text'}
            size='small'
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          >
            H1
          </Button>
        </Tooltip>
        <Tooltip title='Heading 2'>
          <Button
            type={editor.isActive('heading', { level: 2 }) ? 'primary' : 'text'}
            size='small'
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            H2
          </Button>
        </Tooltip>
        <Tooltip title='Heading 3'>
          <Button
            type={editor.isActive('heading', { level: 3 }) ? 'primary' : 'text'}
            size='small'
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            H3
          </Button>
        </Tooltip>

        <Divider type='vertical' />

        <Tooltip title='굵게'>
          <Button
            type={editor.isActive('bold') ? 'primary' : 'text'}
            size='small'
            icon={<BoldOutlined />}
            onClick={() => editor.chain().focus().toggleBold().run()}
          />
        </Tooltip>
        <Tooltip title='기울임'>
          <Button
            type={editor.isActive('italic') ? 'primary' : 'text'}
            size='small'
            icon={<ItalicOutlined />}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          />
        </Tooltip>
        <Tooltip title='밑줄'>
          <Button
            type={editor.isActive('underline') ? 'primary' : 'text'}
            size='small'
            icon={<UnderlineOutlined />}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          />
        </Tooltip>

        <Divider type='vertical' />

        <Tooltip title='순서 없는 목록'>
          <Button
            type={editor.isActive('bulletList') ? 'primary' : 'text'}
            size='small'
            icon={<UnorderedListOutlined />}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          />
        </Tooltip>
        <Tooltip title='순서 있는 목록'>
          <Button
            type={editor.isActive('orderedList') ? 'primary' : 'text'}
            size='small'
            icon={<OrderedListOutlined />}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          />
        </Tooltip>

        <Divider type='vertical' />

        <Tooltip title='링크'>
          <Button
            type={editor.isActive('link') ? 'primary' : 'text'}
            size='small'
            icon={<LinkOutlined />}
            onClick={setLink}
          />
        </Tooltip>

        <Tooltip title='이미지 업로드'>
          <Upload
            accept='image/jpeg,image/png,image/jpg'
            showUploadList={false}
            beforeUpload={(file) => {
              onImageUpload(file);
              return false;
            }}
          >
            <Button type='text' size='small' icon={<PictureOutlined />} loading={imageUploading} />
          </Upload>
        </Tooltip>

        <Tooltip title='구분선'>
          <Button
            type='text'
            size='small'
            icon={<LineOutlined />}
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
          />
        </Tooltip>

        <Divider type='vertical' />

        <Tooltip title='실행 취소'>
          <Button
            type='text'
            size='small'
            icon={<UndoOutlined />}
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          />
        </Tooltip>
        <Tooltip title='다시 실행'>
          <Button
            type='text'
            size='small'
            icon={<RedoOutlined />}
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          />
        </Tooltip>
      </Space>
    </div>
  );
};

export default EditorToolbar;
